import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Copy, Check, Plus, Trash2, Mail } from "lucide-react";
import { api, API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const map = {
    pending: "bg-[color:hsl(var(--dh-ocre)/0.2)] text-[color:hsl(var(--dh-muted))] border-[color:hsl(var(--dh-ocre))]",
    accepted: "bg-[color:hsl(var(--dh-palm)/0.15)] text-[color:hsl(var(--dh-palm))] border-[color:hsl(var(--dh-palm))]",
    declined: "bg-red-50 text-red-700 border-red-300",
    archived: "bg-gray-100 text-gray-500 border-gray-300",
    new: "bg-[color:hsl(var(--dh-terracotta)/0.15)] text-[color:hsl(var(--dh-terracotta))] border-[color:hsl(var(--dh-terracotta))]",
  };
  return <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest border ${map[status] || map.pending}`}>{status}</span>;
}

const HASNA_CHOICE_LABELS = {
  entire: "Villa entière",
  master: "Suite Master",
  confort: "Suite Confort",
  twin: "Suite Twin Palmeraie",
  undecided: "À discuter",
};

const SUITE_OPTIONS = [
  { value: "entire", label: "Villa entière" },
  { value: "master", label: "Suite Master" },
  { value: "confort", label: "Suite Confort" },
  { value: "twin", label: "Suite Twin Palmeraie" },
];

const DEFAULT_LABELS = {
  hasna: [
    { label: "Airbnb — Villa entière", url: "", suite: "entire" },
    { label: "Airbnb — Suite Master", url: "", suite: "master" },
    { label: "Airbnb — Suite Confort", url: "", suite: "confort" },
    { label: "Airbnb — Suite Twin Palmeraie", url: "", suite: "twin" },
    { label: "Booking.com — Villa entière", url: "", suite: "entire" },
  ],
};

function ICalCard({ slug, name }) {
  const { t } = useLang();
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const exportUrl = `${origin}/api/villas/${slug}/ical.ics`;

  useEffect(() => {
    api.get(`/admin/villas/${slug}/ical`).then((r) => {
      const saved = r.data.ical_urls || [];
      setEntries(saved.length ? saved : DEFAULT_LABELS[slug]);
    });
  }, [slug]);

  const update = (i, key, val) => {
    setEntries((prev) => prev.map((e, idx) => (idx === i ? { ...e, [key]: val } : e)));
  };
  const remove = (i) => setEntries((prev) => prev.filter((_, idx) => idx !== i));
  const add = () => setEntries((prev) => [...prev, { label: "", url: "", suite: slug === "hasna" ? "entire" : null }]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ical_urls: entries.filter((e) => (e.url || "").trim()) };
      const r = await api.put(`/admin/villas/${slug}/ical`, payload);
      toast.success(`${name} · ${r.data.ical_urls.length} flux enregistré(s)`);
    } catch (e) {
      toast.error("Erreur — vérifiez les URLs");
    } finally {
      setSaving(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(exportUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div data-testid={`ical-card-${slug}`} className="dh-card p-8 space-y-5">
      <div className="flex items-baseline justify-between">
        <div className="font-serif text-2xl">{name}</div>
        <div className="text-xs text-[color:hsl(var(--dh-muted))]">{entries.filter((e) => e.url).length} flux</div>
      </div>

      <div className="space-y-4">
        {entries.map((e, i) => (
          <div key={i} className={`grid gap-2 items-end ${slug === "hasna" ? "grid-cols-12" : "grid-cols-12"}`}>
            <div className={`${slug === "hasna" ? "col-span-3" : "col-span-4"} space-y-1`}>
              <Label className="dh-overline text-[10px]">Libellé</Label>
              <Input
                value={e.label || ""}
                onChange={(ev) => update(i, "label", ev.target.value)}
                placeholder="Airbnb — Suite X"
                data-testid={`ical-label-${slug}-${i}`}
                className="h-10 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)] text-xs"
              />
            </div>
            {slug === "hasna" && (
              <div className="col-span-3 space-y-1">
                <Label className="dh-overline text-[10px]">Suite</Label>
                <Select value={e.suite || "entire"} onValueChange={(v) => update(i, "suite", v)}>
                  <SelectTrigger data-testid={`ical-suite-${slug}-${i}`} className="h-10 rounded-none border-[color:hsl(var(--dh-ocre)/0.4)] bg-white text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUITE_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className={`${slug === "hasna" ? "col-span-5" : "col-span-7"} space-y-1`}>
              <Label className="dh-overline text-[10px]">URL iCal</Label>
              <Input
                value={e.url || ""}
                onChange={(ev) => update(i, "url", ev.target.value)}
                placeholder="https://…/ical/…ics?t=…"
                data-testid={`ical-url-${slug}-${i}`}
                className="h-10 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)] text-xs"
              />
            </div>
            <button
              type="button"
              onClick={() => remove(i)}
              aria-label="delete"
              data-testid={`ical-remove-${slug}-${i}`}
              className="col-span-1 h-10 grid place-items-center border border-[color:hsl(var(--dh-ocre)/0.4)] text-[color:hsl(var(--dh-muted))] hover:text-red-600 hover:border-red-400 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button type="button" onClick={add} data-testid={`ical-add-${slug}`} className="dh-btn-ghost py-2 px-3 text-xs">
          <Plus className="h-4 w-4" /> Ajouter un flux
        </button>
        <button type="button" onClick={save} disabled={saving} data-testid={`ical-save-${slug}`} className="dh-btn-primary py-2 px-6 text-xs flex-1">
          {saving ? "…" : t.admin.save}
        </button>
      </div>

      {slug === "hasna" && (
        <p className="text-xs text-[color:hsl(var(--dh-muted))] leading-relaxed">
          <b>Astuce :</b> tag chaque flux avec la suite concernée. Une réservation "Villa entière" bloque automatiquement toutes les suites.
        </p>
      )}

      <div className="pt-4 border-t border-[color:hsl(var(--dh-ocre)/0.2)]">
        <Label className="dh-overline">{t.admin.exportUrl}</Label>
        <div className="mt-2 flex items-center gap-2">
          <code className="flex-1 text-xs bg-[color:hsl(var(--dh-sand))] p-3 break-all">{exportUrl}</code>
          <button onClick={copy} className="dh-btn-ghost py-3 px-3" aria-label="copy">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useLang();
  const nav = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("dh_admin_token") : null;
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [villaFilter, setVillaFilter] = useState("all");
  const [hasnaFilter, setHasnaFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (villaFilter !== "all") params.append("villa", villaFilter);
      if (villaFilter === "hasna" && hasnaFilter !== "all") params.append("hasna_choice", hasnaFilter);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const [b, s, l] = await Promise.all([
        api.get(`/admin/bookings${qs}`),
        api.get("/admin/stats"),
        api.get("/admin/family-leads"),
      ]);
      setBookings(b.data);
      setStats(s.data);
      setLeads(l.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("dh_admin_token");
        nav("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [villaFilter, hasnaFilter]);
  if (!token) return <Navigate to="/admin/login" replace />;

  const setStatus = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    toast.success(`→ ${status}`);
    load();
  };

  const setLeadStatus = async (id, status) => {
    await api.patch(`/admin/family-leads/${id}`, { status });
    toast.success(`→ ${status}`);
    load();
  };

  const logout = () => { localStorage.removeItem("dh_admin_token"); nav("/admin/login"); };

  return (
    <div className="dh-container py-16">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="dh-overline">{t.admin.dashboard}</div>
          <h1 className="font-serif text-4xl mt-2">Dar Hasna · Admin</h1>
        </div>
        <div className="flex gap-3">
          <button onClick={load} className="dh-btn-ghost py-2 px-4 text-xs" aria-label="refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
          <button onClick={logout} data-testid="admin-logout" className="dh-btn-ghost py-2 px-4 text-xs">
            <LogOut className="h-4 w-4" /> {t.admin.signOut}
          </button>
        </div>
      </div>

      {stats && (
        <div data-testid="stats-grid" className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-12">
          {[
            { k: "total", v: stats.total },
            { k: "pending", v: stats.pending },
            { k: "accepted", v: stats.accepted },
            { k: "declined", v: stats.declined },
            { k: "villa_hasna", v: stats.villa_hasna },
            { k: "familyLeads", v: stats.family_leads ?? 0 },
          ].map((s) => (
            <div key={s.k} className="dh-card p-6">
              <div className="dh-overline">{t.admin[s.k] || s.k}</div>
              <div className="font-serif text-4xl mt-2">{s.v}</div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="bookings">
        <TabsList className="bg-transparent border-b border-[color:hsl(var(--dh-ocre)/0.2)] rounded-none w-full justify-start h-auto p-0">
          <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[color:hsl(var(--dh-terracotta))] px-6 py-3">{t.admin.bookings}</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[color:hsl(var(--dh-terracotta))] px-6 py-3">{t.admin.familyLeads}</TabsTrigger>
          <TabsTrigger value="calendars" data-testid="tab-calendars" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[color:hsl(var(--dh-terracotta))] px-6 py-3">{t.admin.calendars}</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings" className="mt-8">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <Select value={villaFilter} onValueChange={setVillaFilter}>
              <SelectTrigger data-testid="filter-villa" className="w-56 h-10 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.admin.filterAllVillas}</SelectItem>
                <SelectItem value="hasna">Villa Hasna</SelectItem>
              </SelectContent>
            </Select>
            {villaFilter === "hasna" && (
              <Select value={hasnaFilter} onValueChange={setHasnaFilter}>
                <SelectTrigger data-testid="filter-hasna" className="w-64 h-10 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.admin.allSuites}</SelectItem>
                  <SelectItem value="entire">Villa entière</SelectItem>
                  <SelectItem value="master">Suite Master</SelectItem>
                  <SelectItem value="confort">Suite Confort</SelectItem>
                  <SelectItem value="twin">Suite Twin Palmeraie</SelectItem>
                  <SelectItem value="undecided">À discuter</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {loading ? (
            <div className="text-sm text-[color:hsl(var(--dh-muted))]">…</div>
          ) : bookings.length === 0 ? (
            <div className="text-sm text-[color:hsl(var(--dh-muted))]">Aucune demande.</div>
          ) : (
            <div className="dh-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[color:hsl(var(--dh-sand))]">
                  <tr className="text-start">
                    <th className="text-start p-4 dh-overline">Villa</th>
                    <th className="text-start p-4 dh-overline">{t.admin.dates}</th>
                    <th className="text-start p-4 dh-overline">{t.admin.guest}</th>
                    <th className="text-start p-4 dh-overline">{t.admin.status}</th>
                    <th className="text-start p-4 dh-overline">{t.admin.action}</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t border-[color:hsl(var(--dh-ocre)/0.15)]">
                      <td className="p-4 font-serif capitalize">
                        {b.villa}
                        {b.hasna_choice && (
                          <div className="text-[10px] uppercase tracking-widest text-[color:hsl(var(--dh-terracotta))] mt-1">
                            {HASNA_CHOICE_LABELS[b.hasna_choice] || b.hasna_choice}
                          </div>
                        )}
                      </td>
                      <td className="p-4">{b.check_in} → {b.check_out}<div className="text-xs text-[color:hsl(var(--dh-muted))]">{b.guests} pers · {b.formula}</div></td>
                      <td className="p-4">
                        <div className="font-medium">{b.first_name} {b.last_name}</div>
                        <div className="text-xs text-[color:hsl(var(--dh-muted))]">{b.email} · {b.phone}</div>
                        {b.message && <div className="text-xs italic mt-1 max-w-xs">{b.message}</div>}
                      </td>
                      <td className="p-4"><StatusBadge status={b.status} /></td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          {b.status !== "accepted" && <button data-testid={`accept-${b.id}`} onClick={() => setStatus(b.id, "accepted")} className="text-xs uppercase tracking-widest text-[color:hsl(var(--dh-palm))] hover:underline">{t.admin.accept}</button>}
                          {b.status !== "declined" && <button data-testid={`decline-${b.id}`} onClick={() => setStatus(b.id, "declined")} className="text-xs uppercase tracking-widest text-red-600 hover:underline">{t.admin.decline}</button>}
                          {b.status !== "archived" && <button onClick={() => setStatus(b.id, "archived")} className="text-xs uppercase tracking-widest text-[color:hsl(var(--dh-muted))] hover:underline">{t.admin.archive}</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="leads" className="mt-8">
          {leads.length === 0 ? (
            <div className="text-sm text-[color:hsl(var(--dh-muted))]">Aucun prospect Séjour Famille pour le moment.</div>
          ) : (
            <div className="dh-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[color:hsl(var(--dh-sand))]">
                  <tr className="text-start">
                    <th className="text-start p-4 dh-overline">Prospect</th>
                    <th className="text-start p-4 dh-overline">Formule</th>
                    <th className="text-start p-4 dh-overline">Dates & Composition</th>
                    <th className="text-start p-4 dh-overline">Statut</th>
                    <th className="text-start p-4 dh-overline">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-[color:hsl(var(--dh-ocre)/0.15)]">
                      <td className="p-4">
                        <div className="font-medium">{l.first_name} {l.last_name}</div>
                        <div className="text-xs text-[color:hsl(var(--dh-muted))]">{l.email}</div>
                        {l.phone && <div className="text-xs text-[color:hsl(var(--dh-muted))]">{l.phone}</div>}
                      </td>
                      <td className="p-4 capitalize">{l.formula || "any"}</td>
                      <td className="p-4">
                        <div className="text-xs">{l.stay_dates || "—"}</div>
                        <div className="text-xs text-[color:hsl(var(--dh-muted))]">{l.guests || "—"}</div>
                        {l.message && <div className="text-xs italic mt-1 max-w-xs">{l.message}</div>}
                      </td>
                      <td className="p-4"><StatusBadge status={l.status} /></td>
                      <td className="p-4">
                        <div className="flex gap-2 items-center">
                          <a href={`mailto:${l.email}`} className="text-xs uppercase tracking-widest text-[color:hsl(var(--dh-palm))] hover:underline flex items-center gap-1">
                            <Mail className="h-3 w-3" /> Répondre
                          </a>
                          {l.status !== "archived" && <button onClick={() => setLeadStatus(l.id, "archived")} className="text-xs uppercase tracking-widest text-[color:hsl(var(--dh-muted))] hover:underline">Archiver</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="calendars" className="mt-8 grid xl:grid-cols-2 gap-6">
          <ICalCard slug="hasna" name="Villa Hasna" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
